import React from 'react';
import { useHistory } from 'react-router-dom';
import {
    useGetGlobalStorefrontSettingsQuery,
    useSaveGlobalStorefrontSettingsMutation,
    useSoftDeleteStorefrontMutation
} from '../../store/endpoints/StorefrontSettingsEndpoints';
import GlobalStorefrontSettingsForm from '../../components/Storefront/GlobalStorefrontSettingsForm';
import {
    Checkbox, 
    DefaultButton, 
    Dialog, 
    DialogFooter,
    Icon, 
    MessageBar, 
    MessageBarType,
    Pivot,
    PivotItem,
    PrimaryButton,
    Stack,
    StackItem,
    TooltipHost
} from '@fluentui/react';
import StorefrontPreview from '../../components/Storefront/StorefrontPreview';
import StorefrontsTable from '../../components/Storefront/StorefrontsTable';
import {IGlobalStorefrontSettings, IStorefrontSettings} from '../../models/Storefront';
import NewStorefrontDialog, {CopyStorefrontDialog} from '../../components/Storefront/NewStorefrontPanel';
import StorefrontDetailsPanel from '../../components/Storefront/StorefrontDetailsPanel';
import StorefrontOrdersTable from '../../components/Storefront/Orders/StorefrontOrdersTable';
import StorefrontMonthlyOrdersTable from '../../components/Storefront/StorefrontMonthlyOrdersTable';
import StorefrontProductCount from '../../components/Storefront/StorefrontProductCount';
import PaymentProcessorDetails from '../../components/Storefront/PaymentProcessors/PaymentProcessorDetails';
import StorefrontOpportunityExtensionSelection
    from "../../components/Storefront/StorefrontOpportunityExtensionSelection";
import GlobalStorefrontOpportunityExtensionForm from "../../components/Storefront/GlobalStorefrontOpportunityExtensionForm";
import SubExtensionDropdown from '../../components/ExtensionFolios/SubExtensionDropdown';
import { ExtensionType } from '../../Constants';
import StorefrontThemeManagement from '../../components/Storefront/Themes/StorefrontThemeManagement';
import { RootState } from '../../store/rootReducer';
import { useSelector } from 'react-redux';
import { STOREFRONT_TIER_ENTERPRISE } from '../../models/Settings';
import StorefrontSectionEditor from "../../components/Storefront/StorefrontSectionsEditor";
import StorefrontLinksEditor from "../../components/Storefront/StorefrontLinksEditor";
import StorefrontBannerEditor from "../../components/Storefront/StorefrontBannerEditor";
import {useGetOrganizationEntitlementsQuery} from "../../store/endpoints/OrganizationsEndpoints";
import NumberField from "../../components/Inputs/NumberField";
import StorefrontGlobalQuestionsForm from "../../components/Storefront/StorefrontGlobalQuestionsForm";
import FulfillmentForm from '../../components/Storefront/Fulfillment/FulfillmentForm';
import useCan, { PROCUREMENT } from '../../permissions/ability';


// TODO: DELETE THIS PAGE - Once we have validated everything is working on the new pages.

const StorefrontManagementPage = () => {
    const history = useHistory();
    const organization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const getOrgEntitlements = useGetOrganizationEntitlementsQuery(organization ?? '', {skip: !organization || organization === ''});
    const globalSettings = useGetGlobalStorefrontSettingsQuery();
    const [tempGlobalSettings, setTempGlobalSettings] = React.useState(globalSettings.data);
    const [saveGlobalSettings] = useSaveGlobalStorefrontSettingsMutation();
    const [selectedStorefront, setSelectedStorefront] = React.useState<IStorefrontSettings | undefined>();
    const [selectedCopyStorefront, setSelectedCopyStorefront] = React.useState<IStorefrontSettings | undefined>();
    const [copyStorefront, setCopyStorefront] = React.useState<boolean>(false);
    const [newStorefront, setNewStorefront] = React.useState<boolean>(false);
    const [extension, setExtension] = React.useState<string | undefined>();
    const [enableOpportunityCreation, setEnableOpportunityCreation] = React.useState<boolean>(false);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [confirmDelete, setConfirmDelete] = React.useState<IStorefrontSettings | undefined>();
    const [softDeleteStorefront] = useSoftDeleteStorefrontMutation()
    const canProcurement = useCan(PROCUREMENT);
    const [saveSuccess, setSaveSuccess] = React.useState<boolean>(false);
    React.useEffect(() => {
        setTempGlobalSettings(globalSettings.data);
        if(globalSettings.data?.opportunityExtension) {
            setExtension(globalSettings.data?.opportunityExtension)
        }
        if(globalSettings.data?.enableOpportunityCreation) {
            setEnableOpportunityCreation(globalSettings.data?.enableOpportunityCreation)
        }
    }, [globalSettings.data]);

    const saveSettings = (settings: IGlobalStorefrontSettings) => {
        if (settings == null) return;
        saveGlobalSettings(settings);
    }

    const save = () => {
        if (tempGlobalSettings == null) return;
        setIsSaving(true);
        saveGlobalSettings(tempGlobalSettings).unwrap()
            .then((e) => {
                if (e.success){
                    setSaveSuccess(true);
                    setTimeout(() => setSaveSuccess(false), 3000);
                }
            })
            .finally (() => {setIsSaving(false)});
    }
    
    const saveOpportunityExtensionSelection = (selection?: string) => {
        setExtension(selection)
        setTempGlobalSettings( prevState => {
            saveSettings({...prevState, currency: prevState?.currency ?? '', opportunityExtension: selection });
            return {...prevState, currency: prevState?.currency ?? '', opportunityExtension: selection }} );
    }

    const saveEnableOpportunityExtensionSelection = (selection: boolean) => {
        setEnableOpportunityCreation(selection)
        setTempGlobalSettings( prevState => {
            saveSettings({...prevState, currency: prevState?.currency ?? '', enableOpportunityCreation: selection });
            return {...prevState, currency: prevState?.currency ?? '', enableOpportunityCreation: selection }} );
    }

    return (
        <div>
            <h1>Storefront Management</h1>
            <Pivot overflowBehavior='menu' styles={{itemContainer: {marginTop: '1em'}}}>
                <PivotItem headerText='Global Settings'>
                    <Stack horizontal tokens={{childrenGap: '1em'}}>
                        <div style={{width: '45%', minWidth: '300px'}}>
                            <Stack tokens={{childrenGap: '1em'}}>
                                {tempGlobalSettings && <GlobalStorefrontSettingsForm
                                    globalSettings={tempGlobalSettings}
                                    onChange={setTempGlobalSettings}
                                    />
                                }
                                {saveSuccess &&
                                    <MessageBar messageBarType={MessageBarType.success}>Successfully saved</MessageBar>}
                                <StackItem>
                                    <PrimaryButton
                                        text='Save'
                                        onClick={save}
                                        disabled={isSaving}
                                        />
                                </StackItem>
                            </Stack>
                        </div>
                        <div style={{width: '45%', minWidth: '500px'}}>
                            <StorefrontPreview globalSettings={tempGlobalSettings} />
                        </div>
                    </Stack>
                </PivotItem>
                <PivotItem headerText='Sections'>
                    <Stack horizontal tokens={{childrenGap: '1em'}}>
                        <StorefrontSectionEditor isGlobal={true} globalSettings={tempGlobalSettings} />
                    </Stack>
                </PivotItem>
                <PivotItem headerText='Links'>
                    <Stack horizontal tokens={{childrenGap: '1em'}}>
                        <StorefrontLinksEditor isGlobal={true} />
                    </Stack>
                </PivotItem>
                <PivotItem headerText='Banners'>
                    <Stack tokens={{childrenGap: '1em'}}>
                        <div style={{width: '200px'}}>
                            <NumberField toolTip={'Define a default height for the banner container. Recommended for when your banners vary in dimensions to prevent screen jumping.'} label={'Banner Height'} value={tempGlobalSettings?.bannerHeight} onChange={(v) => setTempGlobalSettings( prevState => {
                                if (prevState == null) return;
                                saveSettings({...prevState, bannerHeight: v});
                                return {...prevState, bannerHeight: v }} )} suffix={'px'}/>
                        </div>
                        <StorefrontBannerEditor
                            isGlobal={true}
                        />
                    </Stack>
                </PivotItem>
                <PivotItem headerText='Storefronts'>
                    <h2>Storefronts</h2>
                    <Stack horizontal verticalAlign="center" tokens={{childrenGap: '1em'}} style={{marginBottom: '1em'}}>
                        <DefaultButton 
                            text="Storefront Dashboard" 
                            iconProps={{iconName: 'ViewDashboard'}} 
                            onClick={() => history.push('/settings/storefront/dashboard')}
                        />
                    </Stack>
                    <StorefrontsTable 
                        onCopyClick={(sf) => {setSelectedCopyStorefront(sf); setCopyStorefront(true)}} 
                        onDetailsClick={setSelectedStorefront} 
                        onNewClick={() => setNewStorefront(true)} 
                        onDeleteClick={(sf) => setConfirmDelete(sf)}
                    />
                    <NewStorefrontDialog
                        isOpen={newStorefront}
                        globalSettings={tempGlobalSettings}
                        onAddStorefront={() => setNewStorefront(false)}
                        onClose={() => setNewStorefront(false)}
                        />
                    <Dialog
                        hidden={confirmDelete == undefined}
                        modalProps={{
                            isBlocking: true,
                            onDismiss:() => {
                                setConfirmDelete(undefined)
                            }
                        }}
                        dialogContentProps={{
                            title: 'Confirm Delete',
                            subText: `Are you sure you want to delete ${confirmDelete?.name}? This action cannot be undone.`,
                        }}
                    >
                        <DialogFooter>
                            <DefaultButton
                                primary
                                text='Confirm'
                                onClick={() => {
                                    if (confirmDelete?.id)
                                        softDeleteStorefront(confirmDelete.id).then(() => { setConfirmDelete(undefined) });
                                }}
                            />
                            <DefaultButton
                                text='Cancel'
                                onClick={() => {
                                    setConfirmDelete(undefined)
                                }}
                            />
                        </DialogFooter>
                    </Dialog>
                    <CopyStorefrontDialog
                        isOpen={copyStorefront}
                        settings={selectedCopyStorefront}
                        onAddStorefront={() => setCopyStorefront(false)}
                        onClose={() => setCopyStorefront(false)}
                    />
                    {selectedStorefront?.id && 
                        <StorefrontDetailsPanel
                            globalSettings={tempGlobalSettings}
                            isOpen={selectedStorefront !== undefined}
                            storefrontId={selectedStorefront.id}
                            onClose={() => setSelectedStorefront(undefined)}
                        />
                    }
                </PivotItem>

                <PivotItem headerText='Questions'>
                    <StorefrontGlobalQuestionsForm globalSettings={tempGlobalSettings} />
                </PivotItem>
                {getOrgEntitlements.data?.storefrontTier === STOREFRONT_TIER_ENTERPRISE && <PivotItem headerText='Themes'>
                    <h2>Custom Themes</h2>
                    <StorefrontThemeManagement />
                </PivotItem>}
                <PivotItem headerText='Payments & Billing'>
                    <h2>Payment Processing</h2>
                    <PaymentProcessorDetails />
                    <h2>Taxes</h2>
                    <Stack tokens={{childrenGap: '1em',}}>
                        <SubExtensionDropdown
                            isGlobal={true}
                            type={ExtensionType.Tax}
                            label='Tax Extension'
                            extension={tempGlobalSettings?.taxExtension}
                            onChange={(extension) => setTempGlobalSettings( prevState => {
                                if (prevState == null) return;
                                saveSettings({...prevState, taxExtension: extension});
                                return {...prevState, taxExtension: extension }} )}
                        />
                        <Checkbox
                            label='Collect Tax'
                            checked={tempGlobalSettings?.collectTax}
                            onChange={(e, v) => setTempGlobalSettings( prevState => {
                                if (prevState == null) return;
                                saveSettings({...prevState, collectTax: v ?? false});
                                return {...prevState, collectTax: v ?? false }} )}
                        />
                    </Stack>
                </PivotItem>
                <PivotItem headerText='CRM'>
                    <h2>
                        Locations&nbsp;
                        <TooltipHost
                            content={'If set, this allows customers to look up locations in your CRM for their company to auto-fill shipping & billing information'}
                            >
                                <Icon style={{fontSize:'14px', cursor:'pointer'}} iconName='info' />
                        </TooltipHost>
                    </h2>
                    <Stack tokens={{childrenGap: '1em'}}>
                        <SubExtensionDropdown
                            isGlobal={true}
                            type={ExtensionType.Locations}
                            label='Location Lookup Extension'
                            extension={tempGlobalSettings?.locationLookupExtension}
                            onChange={(extension) => setTempGlobalSettings( prevState => {
                                if (prevState == null) return;
                                saveSettings({...prevState, locationLookupExtension: extension});
                                return {...prevState, locationLookupExtension: extension }} )}
                        />
                        <Checkbox
                            label='Enable Location Lookup'
                            checked={tempGlobalSettings?.locationLookupEnabled}
                            onChange={(e, v) => setTempGlobalSettings( prevState => {
                                if (prevState == null) return;
                                saveSettings({...prevState, locationLookupEnabled: v ?? false});
                                return {...prevState, locationLookupEnabled: v ?? false }} )}
                        />
                    </Stack>

                    <h2>
                        Customers&nbsp;
                        <TooltipHost
                            content={'If set, this extension will be used during customer signup to search your CRM for a related record.'}
                        >
                            <Icon style={{fontSize:'14px', cursor:'pointer'}} iconName='info' />
                        </TooltipHost>
                    </h2>
                    <Stack tokens={{childrenGap: '1em'}}>
                        <SubExtensionDropdown
                            isGlobal={true}
                            type={ExtensionType.StorefrontCustomer}
                            label='Customer Lookup Extension'
                            extension={tempGlobalSettings?.customerLookupExtension}
                            onChange={(extension) => setTempGlobalSettings( prevState => {
                                if (prevState == null) return;
                                saveSettings({...prevState, customerLookupExtension: extension});
                                return {...prevState, customerLookupExtension: extension }} )}
                        />
                    </Stack>

                    <h2>Opportunities</h2>
                    <StorefrontOpportunityExtensionSelection
                        extension={extension}
                        onChange={saveOpportunityExtensionSelection}
                    />
                    <br></br>
                    <Checkbox
                        label="Enable Opportunity Creation"
                        checked={enableOpportunityCreation}
                        onChange={(e, v) => saveEnableOpportunityExtensionSelection(v ?? false)}
                    />
                    <br></br>
                    {enableOpportunityCreation &&

                    <GlobalStorefrontOpportunityExtensionForm
                        subExtensionFolioCompositeId={extension}
                    />
                    }
                </PivotItem>
                <PivotItem headerText='Orders'>
                    <h2>Orders</h2>
                    <StorefrontOrdersTable />
                </PivotItem>
                <PivotItem headerText='Usage'>
                    <h2>Products</h2>
                    <StorefrontProductCount />
                    <h2>Monthly Orders</h2>
                    <StorefrontMonthlyOrdersTable />
                </PivotItem>
                {canProcurement && <PivotItem headerText='Fulfillment'>
                    {tempGlobalSettings != null
                        ? <FulfillmentForm
                            globalSettings={tempGlobalSettings}
                            onGlobalSettingsChange={setTempGlobalSettings}
                        />
                        : null}
                    <PrimaryButton
                        text='Save'
                        onClick={save}
                        disabled={isSaving}
                        />
                </PivotItem>}
            </Pivot>
        </div>
    );
};

export default StorefrontManagementPage;